/**
 * MeterCard Component (renamed from MeterTariffCard)
 *
 * Renders a single meter with its tariff selector, load profile,
 * and per-app configuration. Used inside MetersAndTariffsSection.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    RadioGroup,
    Radio,
    FormControlLabel,
    Button,
    Tooltip,
    CircularProgress,
    TextField,
    MenuItem,
} from '@mui/material';
import { ElectricBolt, EvStation, Search as SearchIcon, CheckCircle } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '../core/theme';
import { useThemeMode } from '../theme-mode/index.js';
import { UnifiedTariffSelector } from '../tariff/index';
import MeterLoadProfile from './MeterLoadProfile';

function MeterCard({
    meterId,        // "main" or "ev_submeter"
    meter,          // { name, tariff: { id, name, utility, schedule, voltage }, load_profile: { ... } }
    onTariffSelect, // (tariffObject) => void
    appsOnMeter,    // [{ name: "Site DNA", detail: "Main System" }, ...]
    disabled,
    // Load profile props
    onLoadProfileUpload,
    onLoadProfileRemove,
    // App config props
    onAppConfigUpdate,
    project,
    // Decomposition props
    onDecompose,    // () => Promise<void> — triggers decomposition for Flux COLD
    // Site electrical props (main meter only)
    siteAmps,              // string — amperage input value
    serviceVoltage,        // string — voltage dropdown value
    onSiteAmpsChange,      // (string) => void — update amps state
    onServiceVoltageChange,// (string) => void — update voltage + save
    onSiteElectricalSave,  // () => void — persist on amps blur
}) {
    const { isRetro } = useThemeMode();
    const [selectedTariff, setSelectedTariff] = useState(null);
    const [decomposing, setDecomposing] = useState(false);

    // Hydrate selected tariff from meter data
    useEffect(() => {
        if (meter?.tariff?.id) {
            setSelectedTariff({
                id: meter.tariff.id,
                utility_name: meter.tariff.utility || '',
                name: meter.tariff.name || '',
                rate_schedule_name: meter.tariff.schedule || '',
                voltage_level: meter.tariff.voltage || '',
            });
        }
    }, [meter]);

    const handleTariffSelect = (tariff) => {
        if (!tariff) return;
        setSelectedTariff(tariff);
        onTariffSelect(tariff);
    };

    const isMain = meterId === 'main';
    const MeterIcon = isMain ? ElectricBolt : EvStation;
    const meterLabel = meter?.name || (isMain ? 'Facility Meter' : 'EV Submeter');
    const hasLoad8760 = meter?.load_profile?.loads_kw?.length === 8760;

    return (
        <Paper
            sx={{
                p: 3,
                bgcolor: isRetro ? vortxColors.charcoal : 'background.paper',
                border: `1px solid ${isRetro ? vortxColors.teal : 'rgba(0,0,0,0.12)'}`,
            }}
        >
            {/* Meter Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <MeterIcon sx={{ color: vortxColors.gold, fontSize: 24 }} />
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: vortxTypography.heading,
                        color: isRetro ? vortxColors.white : 'text.primary',
                        fontWeight: 700,
                    }}
                >
                    {meterLabel}
                </Typography>
                {meter?.tariff?.name && (
                    <Chip
                        label={meter.tariff.name}
                        size="small"
                        sx={{
                            bgcolor: 'rgba(178, 140, 80, 0.15)',
                            color: vortxColors.gold,
                            fontWeight: 600,
                        }}
                    />
                )}
                {hasLoad8760 && (
                    <Chip
                        label={`${Math.round(meter.load_profile.metrics?.peak_kw || 0)} kW peak`}
                        size="small"
                        sx={{
                            bgcolor: 'rgba(178, 140, 80, 0.1)',
                            color: vortxColors.gold,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                        }}
                    />
                )}
            </Box>

            {/* Site Electrical Service (main meter only) */}
            {isMain && onSiteAmpsChange && (() => {
                const amps = parseFloat(siteAmps);
                const volts = parseInt(serviceVoltage);
                const computedKw = (amps > 0 && volts > 0)
                    ? Math.round(amps * volts * Math.sqrt(3) / 100) / 10
                    : null;
                const fieldStyles = {
                    '& .MuiOutlinedInput-root': {
                        color: isRetro ? vortxColors.white : 'text.primary',
                        '& fieldset': { borderColor: isRetro ? vortxColors.teal : 'rgba(0,0,0,0.23)' },
                        '&:hover fieldset': { borderColor: vortxColors.gold },
                        '&.Mui-focused fieldset': { borderColor: vortxColors.gold, borderWidth: 2 },
                    },
                    '& .MuiInputLabel-root': {
                        color: isRetro ? vortxColors.stone : 'text.secondary',
                        '&.Mui-focused': { color: vortxColors.gold },
                    },
                };
                const VOLTAGES = [
                    { value: '120', label: '120V' },
                    { value: '208', label: '208V' },
                    { value: '240', label: '240V' },
                    { value: '277', label: '277V' },
                    { value: '480', label: '480V' },
                    { value: '600', label: '600V' },
                    { value: '2400', label: '2.4 kV' },
                    { value: '4160', label: '4.16 kV' },
                ];
                return (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label="Service (Amps)"
                            value={siteAmps || ''}
                            onChange={(e) => onSiteAmpsChange(e.target.value)}
                            onBlur={() => onSiteElectricalSave && onSiteElectricalSave()}
                            size="small"
                            type="number"
                            placeholder="e.g. 400"
                            sx={{ ...fieldStyles, width: 140 }}
                            inputProps={{ min: 0, step: 'any' }}
                        />
                        <TextField
                            select
                            label="Voltage"
                            value={serviceVoltage || ''}
                            onChange={(e) => onServiceVoltageChange && onServiceVoltageChange(e.target.value)}
                            size="small"
                            sx={{ ...fieldStyles, width: 130 }}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            bgcolor: isRetro ? vortxColors.charcoal : '#fff',
                                            border: isRetro ? `1px solid ${vortxColors.teal}` : 'none',
                                            '& .MuiMenuItem-root': {
                                                color: isRetro ? vortxColors.white : 'text.primary',
                                                fontSize: '0.875rem',
                                                '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.15)' },
                                                '&.Mui-selected': { bgcolor: 'rgba(178, 140, 80, 0.25)' },
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            {VOLTAGES.map((v) => (
                                <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                            ))}
                        </TextField>
                        {computedKw !== null && (
                            <Box sx={{
                                px: 1.5, py: 0.5,
                                bgcolor: 'rgba(178, 140, 80, 0.1)',
                                border: `1px solid rgba(178, 140, 80, 0.25)`,
                                borderRadius: 1,
                            }}>
                                <Typography sx={{
                                    color: vortxColors.stone,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.6rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}>
                                    Capacity
                                </Typography>
                                <Typography sx={{
                                    color: vortxColors.gold,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                }}>
                                    {computedKw} kW
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            })()}

            {/* Tariff Selector */}
            <Box sx={{ mb: 2 }}>
                <UnifiedTariffSelector
                    selectedTariff={selectedTariff}
                    onTariffSelect={handleTariffSelect}
                    matchSource="vortx"
                    disabled={disabled}
                />
            </Box>

            {/* Load Profile */}
            <MeterLoadProfile
                meterId={meterId}
                loadProfile={meter?.load_profile}
                onUpload={onLoadProfileUpload}
                onRemove={onLoadProfileRemove}
                projectId={project?.id}
            />

            {/* Apps on this meter */}
            {appsOnMeter && appsOnMeter.length > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(63, 106, 100, 0.4)' }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: vortxColors.stone,
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            fontWeight: 600,
                            display: 'block',
                            mb: 1,
                        }}
                    >
                        Apps on this meter
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {appsOnMeter.map((app, idx) => {
                            const isFluxCold = app.name === 'Flux COLD';
                            const coldRelationship = project?.apps?.flux_cold?.load_relationship || 'included';
                            const meterHasLoad = meter?.load_profile?.loads_kw?.length > 0;

                            return (
                                <Box key={idx}>
                                    {/* App chip */}
                                    <Chip
                                        label={`${app.name} - ${app.detail}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            borderColor: vortxColors.teal,
                                            color: vortxColors.stone,
                                        }}
                                    />

                                    {/* Flux COLD specific: load relationship selector */}
                                    {isFluxCold && (
                                        <Box sx={{ ml: 2, mt: 1 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: vortxColors.stone,
                                                    fontSize: '0.7rem',
                                                    display: 'block',
                                                    mb: 0.5,
                                                }}
                                            >
                                                Load relationship to facility meter:
                                            </Typography>
                                            <RadioGroup
                                                value={coldRelationship}
                                                onChange={(e) => {
                                                    if (onAppConfigUpdate) {
                                                        onAppConfigUpdate('flux_cold', { load_relationship: e.target.value });
                                                    }
                                                }}
                                                sx={{ ml: 0.5 }}
                                            >
                                                <FormControlLabel
                                                    value="included"
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                color: vortxColors.stone,
                                                                '&.Mui-checked': { color: vortxColors.gold },
                                                                p: 0.5,
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="caption" sx={{ color: isRetro ? vortxColors.white : 'text.primary', fontSize: '0.75rem' }}>
                                                            Included in facility meter
                                                        </Typography>
                                                    }
                                                    sx={{ m: 0, height: 28 }}
                                                />
                                                <FormControlLabel
                                                    value="additive"
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                color: vortxColors.stone,
                                                                '&.Mui-checked': { color: vortxColors.gold },
                                                                p: 0.5,
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="caption" sx={{ color: isRetro ? vortxColors.white : 'text.primary', fontSize: '0.75rem' }}>
                                                            Additional load
                                                        </Typography>
                                                    }
                                                    sx={{ m: 0, height: 28 }}
                                                />
                                            </RadioGroup>

                                            {/* Decompose — only when included AND meter has 8760 load */}
                                            {coldRelationship === 'included' && meterHasLoad && (() => {
                                                const coldDecomp = meter?.load_profile?.decompositions?.flux_cold;
                                                const hasResults = coldDecomp && coldDecomp.extracted_loads_kw?.length === 8760;
                                                const confidence = hasResults ? coldDecomp.confidence : null;

                                                const handleDecompose = async () => {
                                                    if (!onDecompose || decomposing) return;
                                                    setDecomposing(true);
                                                    try {
                                                        await onDecompose();
                                                    } finally {
                                                        setDecomposing(false);
                                                    }
                                                };

                                                // Compute stats from decomposition results
                                                const extractedPeak = hasResults
                                                    ? Math.max(...coldDecomp.extracted_loads_kw)
                                                    : 0;
                                                const extractedAnnual = hasResults
                                                    ? coldDecomp.extracted_loads_kw.reduce((s, v) => s + v, 0)
                                                    : 0;
                                                const extractedPct = hasResults && meter.load_profile.metrics?.annual_kwh > 0
                                                    ? (extractedAnnual / meter.load_profile.metrics.annual_kwh) * 100
                                                    : 0;

                                                return (
                                                    <Box sx={{ mt: 0.5, ml: 0.5 }}>
                                                        {/* Button row */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Tooltip
                                                                title="Extract controllable cold storage loads from this meter's data using Flux COLD's thermodynamic model."
                                                                arrow
                                                                placement="right"
                                                            >
                                                                <span>
                                                                    <Button
                                                                        size="small"
                                                                        disabled={!onDecompose || decomposing}
                                                                        onClick={handleDecompose}
                                                                        startIcon={
                                                                            decomposing
                                                                                ? <CircularProgress size={12} sx={{ color: vortxColors.gold }} />
                                                                                : <SearchIcon sx={{ fontSize: 14 }} />
                                                                        }
                                                                        sx={{
                                                                            color: hasResults ? vortxColors.gold : vortxColors.stone,
                                                                            textTransform: 'none',
                                                                            fontSize: '0.7rem',
                                                                            '&:hover': {
                                                                                bgcolor: 'rgba(178, 140, 80, 0.1)',
                                                                                color: vortxColors.gold,
                                                                            },
                                                                            '&.Mui-disabled': {
                                                                                color: 'rgba(166, 161, 155, 0.4)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        {decomposing
                                                                            ? 'Decomposing...'
                                                                            : hasResults
                                                                                ? 'Re-Decompose Load'
                                                                                : 'Decompose Load'}
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>

                                                            {/* Confidence badge */}
                                                            {hasResults && confidence != null && (
                                                                <Chip
                                                                    icon={<CheckCircle sx={{ fontSize: 12 }} />}
                                                                    label={`${Math.round(confidence * 100)}% confidence`}
                                                                    size="small"
                                                                    sx={{
                                                                        height: 20,
                                                                        fontSize: '0.6rem',
                                                                        fontWeight: 600,
                                                                        bgcolor: confidence >= 0.7
                                                                            ? 'rgba(46, 125, 50, 0.2)'
                                                                            : confidence >= 0.4
                                                                                ? 'rgba(237, 108, 2, 0.2)'
                                                                                : 'rgba(211, 47, 47, 0.2)',
                                                                        color: confidence >= 0.7
                                                                            ? '#66bb6a'
                                                                            : confidence >= 0.4
                                                                                ? '#ffa726'
                                                                                : '#ef5350',
                                                                        borderColor: 'transparent',
                                                                        '& .MuiChip-icon': {
                                                                            color: 'inherit',
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>

                                                        {/* Decomposition stats — shown when results exist */}
                                                        {hasResults && (
                                                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, ml: 0.5 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: vortxColors.stone,
                                                                        fontSize: '0.6rem',
                                                                        fontFamily: vortxTypography.mono,
                                                                    }}
                                                                >
                                                                    Peak: {Math.round(extractedPeak)} kW
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: vortxColors.stone,
                                                                        fontSize: '0.6rem',
                                                                        fontFamily: vortxTypography.mono,
                                                                    }}
                                                                >
                                                                    Annual: {Math.round(extractedAnnual).toLocaleString()} kWh
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: vortxColors.stone,
                                                                        fontSize: '0.6rem',
                                                                        fontFamily: vortxTypography.mono,
                                                                    }}
                                                                >
                                                                    {extractedPct.toFixed(1)}% of meter
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

export default MeterCard;
